import { useEffect, useMemo } from 'react';
import { useBusinessNameQuery } from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import { BusinessNameRequest, BusinessNameResponse } from 'store/api/apiTypes';

/**
 * useBusinessName
 * Fetches Business Name information completeness rule for selected project domain.
 */
export const useBusinessName = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );

  const activeReport = useAppSelector(state => state.history.activeReport);
  const queryArgs = useMemo<BusinessNameRequest | null>(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain: selectedProject.domain, reportId: activeReport?.id };
  }, [selectedProject, activeReport]);

  const { data, error, isLoading, isFetching, refetch } = useBusinessNameQuery(
    queryArgs as BusinessNameRequest,
    {
      skip: !queryArgs,
      refetchOnMountOrArgChange: true,
    }
  );

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  useEffect(() => {
    if (error) handleApiError(error as any);
  }, [error, handleApiError]);

  usePollingOnError(queryArgs, error, refetch, data);

  const rule = data as BusinessNameResponse | undefined;

  return {
    businessName: rule,
    pageUrls: rule?.page_urls_analyzed || [],
    identified: rule?.business_name_identified,
    extraction: rule?.business_name_extraction,
    scoring: rule?.scoring_breakdown || {},
    finalScore: rule?.final_score,
    finalCalculation: rule?.final_calculation,
    overallAssessment: rule?.overall_assessment,
    priorityImprovements: rule?.priority_improvements || [],
    isLoading,
    isFetching,
    error,
    refetchBusinessName: refetch,
    showBusinessNameErrorModal: showErrorModal,
    retryBusinessName: handleRetry,
    hideBusinessNameErrorModal: hideErrorModal,
    hasBusinessName: !!rule,
    queryArgs,
  };
};
